<template>
  <div class="flex flex-col space-y-5">
    <div class="flex space-x-5">
      <div class="flex flex-1 flex-col space-y-2">
        <BaseSelectMultiple
          v-if="metadata?.companies?.length > 1"
          :label="$t('attributes.company')"
          :placeholder="$t('attributes.company')"
          name="company_id"
          :value="null"
          :options="metadata.companies"
          @setSelected.self="handleFilterTable"
        />
        <BaseSelectMultiple
          v-if="metadata?.status && showStatusFilter"
          :label="$t('attributes.status')"
          :placeholder="$t('attributes.status')"
          name="status_code"
          :value="null"
          :options="metadata.status"
          @setSelected.self="handleFilterTable"
        />

        <BaseSelectMultiple
          v-if="metadata.customers && !isCustomerUser"
          :label="$t('attributes.customer')"
          :placeholder="$t('attributes.customer')"
          name="customer_search"
          :options="metadata.customers"
          :searchable="true"
          @setSelected="handleFilterTable"
        />

        <BaseSelectMultiple
          v-if="serverFilters && metadata?.fiscal_years?.length"
          :label="$t('attributes.fiscal_year')"
          :placeholder="$t('attributes.fiscal_year')"
          name="fiscal_year"
          :options="metadata.fiscal_years"
          :default-value="
            defaultCurrentFiscalYear ? [metadata.fiscal_years[0]] : []
          "
          @setSelected="handleFilterTable"
        />

        <BaseSearchInput
          v-if="this.search"
          groupClass="mt-3"
          @typeInput="filterList"
        />
      </div>
      <div class="flex flex-1 flex-col space-y-2">
        <BaseDateRangeInput
          v-model="emission_date"
          :label="$t('attributes.emission_date')"
          name="emission_date"
          separator=" - "
          :formatter="date_formatter"
        />
        <BaseDateRangeInput
          v-model="due_date"
          :label="$t('attributes.due_date')"
          name="due_date"
          separator=" - "
          :formatter="date_formatter"
        />
        <div class="flex items-center justify-end space-x-3 pt-3">
          <ColumnVisibilityDropDownComponent v-model="columnVisibility" />

          <BaseButton
            color="secondary"
            icon="sync"
            :tooltip="$t('button.refresh')"
            @click="$emit('refresh-click')"
          />
        </div>
      </div>
    </div>

    <div :class="divClasses">
      <table class="table" :class="tableClasses">
        <!--#region header -->
        <thead>
          <tr :class="theadRowClasses">
            <BaseThComponent
              v-for="column in columnVisibility.filter((item) => !item.hidden)"
              :key="column.field"
              :column="column"
              :th-classes="thClasses"
              :width="column.width"
              :currentSort="currentSort"
              @sortColumn="handleSortColumn"
            />
          </tr>
        </thead>
        <!--#endregion -->

        <tbody>
          <tr
            v-for="(model, index) in filteredModels"
            :key="index"
            @click="this.$emit('rowClick', model.id)"
            :class="[trClasses, generateStripedClass(index)]"
          >
            <td
              v-for="(column, index) in columnVisibility?.filter(
                (item) => !item.hidden
              )"
              :key="index"
              class="dark:border-dark-5 whitespace-nowrap"
              :class="tdClasses"
            >
              <div
                v-if="column.type === 'label'"
                class="flex flex-col items-start gap-1"
              >
                <BaseLabel
                  :icon="column.metadata.icon"
                  :content="model[column.metadata.value]"
                  :color="model[column.metadata.color]"
                />
                <PeppolStatusBadge :invoice="model" />
              </div>
              <ContentTableCell v-else :model="model" :column="column" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { baseURL } from '@u/apiClient'
import { mapActions } from 'vuex'
import moment from 'moment'

import printJS from 'print-js'

/** Table filter name -> API query param (GET /api/invoicing). */
const SERVER_FILTERS = {
  company_id: 'company_id',
  customer_search: 'customer_id',
  status_code: 'status',
  fiscal_year: 'fiscal_year'
}
import apiClient from '@u/apiClient'
import searchListHelper from '@u/searchListHelper'
import PeppolStatusBadge from '@c/Partner/Peppol/PeppolStatusBadge.vue'

export default {
  name: 'InvoicingIndexTable',
  components: { PeppolStatusBadge },
  props: {
    /**
     * Company, customer, status and fiscal year filters are applied by the
     * API (TheInvoicingIndexPage refetches on `server-filters-change`); the
     * search and the date ranges stay client-side.
     */
    serverFilters: {
      type: Boolean,
      required: false,
      default: false
    },
    showStatusFilter: {
      type: Boolean,
      required: false,
      default: true
    },
    /** Fiscal year filter starts on the current exercise (the API default). */
    defaultCurrentFiscalYear: {
      type: Boolean,
      required: false,
      default: true
    },
    columns: {
      type: Array,
      required: true
    },
    rowClick: {
      type: String,
      required: false,
      default: ''
    },
    search: {
      type: String,
      default: false
    },
    metadata: {
      type: Array,
      required: true
    },
    modelList: {
      type: Array,
      required: true
    },
    isBordered: {
      type: Boolean,
      required: false,
      default: false
    },
    isHoverable: {
      type: Boolean,
      required: false,
      default: false
    },
    isSmallTable: {
      type: Boolean,
      required: false,
      default: false
    },
    isResponsive: {
      type: Boolean,
      required: false,
      default: false
    },
    isStriped: {
      type: Boolean,
      required: false,
      default: false
    },
    theadRowClasses: {
      type: String,
      required: false,
      default: ''
    }
  },
  emits: ['refresh-click', 'server-filters-change'],
  data() {
    return {
      searchTerms: '',
      sortDirection: null,
      sortField: '',
      currentSort: '',
      emission_date: '',
      due_date: '',
      date_formatter: {
        date: 'DD/MM/YYYY',
        month: 'MM'
      },
      // The preselected exercise is in the filter, so the PDF and CSV
      // exports cover what the list shows.
      filter:
        this.serverFilters &&
        this.defaultCurrentFiscalYear &&
        this.metadata?.fiscal_years?.length
          ? { fiscal_year: [this.metadata.fiscal_years[0]] }
          : {},
      columnVisibilityState: null
    }
  },
  computed: {
    filteredCheckList() {
      let tempChecks = this.model

      if (this.filters.length !== 0) {
        tempChecks = tempChecks.filter((row) =>
          this.filters.includes(row.customer_name)
        )
      }
      return tempChecks
    },
    tableTitle() {
      return this.$route.name === 'invoice_unpaid_index_route'
        ? this.$t('global.unpaid')
        : this.$route.name === 'invoice_paid_index_route'
          ? this.$t('global.paid')
          : this.$t('enums.invoicing.type.' + this.$route.meta.doc_type)
    },
    tdClasses() {
      return this.isBordered ? 'border' : 'border-b'
    },
    thClasses() {
      return this.isBordered ? 'border' : ''
    },
    trClasses() {
      return this.isHoverable ? 'hover:bg-gray-200 dark:hover:bg-gray-600' : ''
    },
    tableClasses() {
      return this.isSmallTable ? 'table--sm' : ''
    },
    divClasses() {
      return this.isResponsive ? 'overflow-x-auto' : ''
    },

    filteredModels() {
      let tempModels = this.modelList
      if (this.searchTerms.length >= 1) {
        tempModels = tempModels.filter((model) => {
          let search = this.searchTerms.length
            ? this.searchTerms.toLowerCase()
            : ''
          var values = this.filtrableColumns
          var flag = false
          values.forEach((val) => {
            if (
              model[val] &&
              String(model[val]).toLowerCase().indexOf(search) > -1
            ) {
              flag = true
              return
            }
          })
          if (flag) return model
        })
      }
      if (this.filter.company_id && !this.serverFilters) {
        tempModels = tempModels?.filter((model) => {
          return model.company_id === this.filter.company_id[0].id
        })
      }
      if (this.filter.status_code && this.serverFilters) {
        // filtered by the API
      } else if (this.filter.status_code) {
        // console.log(this.filter.status_code)
        // tempModels = tempModels?.filter(model => {
        //   return model.status_code === this.filter.status_code[0].id
        //
        // })
        let arrayOfAllStatusIdInFilter = []
        this.filter.status_code.map((status) => {
          arrayOfAllStatusIdInFilter.push(status.id)
        })
        tempModels = tempModels?.filter((model) =>
          arrayOfAllStatusIdInFilter.includes(model.status_code)
        )
      } else {
        if (this.$route.name !== 'invoice_paid_index_route') {
          tempModels = tempModels?.filter(
            (model) => model.status_code !== 'PAI'
          )
        }
      }
      if (this.filter.due_date) {
        const search_start = moment(
          this.filter.due_date[0],
          'DD/MM/YYYY'
        ).startOf('day')
        const search_end = moment(this.filter.due_date[1], 'DD/MM/YYYY').endOf(
          'day'
        )
        tempModels = tempModels?.filter((model) => {
          const model_date = moment(model.due_date, 'DD/MM/YYYY').add(
            1,
            'minutes'
          )
          return model_date.isBetween(search_start, search_end)
        })
      }
      if (this.filter.emission_date) {
        const search_start = moment(
          this.filter.emission_date[0],
          'DD/MM/YYYY'
        ).startOf('day')
        const search_end = moment(
          this.filter.emission_date[1],
          'DD/MM/YYYY'
        ).endOf('day')
        tempModels = tempModels?.filter((model) => {
          const model_date = moment(model.emission_date, 'DD/MM/YYYY').add(
            1,
            'minutes'
          )
          return model_date.isBetween(search_start, search_end)
        })
      }
      if (this.filter.customer_search && !this.serverFilters) {
        let arrayOfAllCustomersIdInFilter = []
        this.filter.customer_search.map((customer) => {
          arrayOfAllCustomersIdInFilter.push(customer.id)
        })
        tempModels = tempModels?.filter((model) =>
          arrayOfAllCustomersIdInFilter.includes(model.customer_id)
        )
      }
      if (this.currentSort && this.sortDirection) {
        const column = this.currentSort
        const order = this.sortDirection === 'up' ? 1 : -1
        tempModels = [...(tempModels ?? [])].sort((a, b) => {
          const fa = searchListHelper.sortValue(a[column])
          const fb = searchListHelper.sortValue(b[column])
          if (fa < fb) return -order
          if (fa > fb) return order
          return 0
        })
      }
      return tempModels
    },
    // A customer only sees their own invoices: no customer filter or column.
    isCustomerUser() {
      return this.$store.getters['auth/getAuthRole'] === 'customer-user'
    },
    filtrableColumns() {
      return this.columns
        .filter(function (column) {
          return column.searchable === true
        })
        .map((obj) => {
          return obj.field
        })
    },
    columnVisibility: {
      // The API's columns, with only the hidden flags remembered from
      // localStorage: column changes on the API side (type, sorter) apply.
      get() {
        if (this.columnVisibilityState) return this.columnVisibilityState
        let stored = []
        try {
          stored =
            JSON.parse(localStorage.getItem('InvoiceColumnsVisibility')) ?? []
        } catch (_) {}
        const hidden = Object.fromEntries(
          stored
            .filter((column) => column?.field)
            .map((column) => [column.field, !!column.hidden])
        )
        return this.columns
          .filter(
            (column) => !(this.isCustomerUser && column.field === 'customer')
          )
          .map((column) =>
            column.field in hidden
              ? { ...column, hidden: hidden[column.field] }
              : column
          )
      },
      set(columns) {
        this.columnVisibilityState = columns
      }
    }
  },
  methods: {
    ...mapActions({
      fetchInvoicesCSV: 'invoicing/fetchInvoicesCSV'
    }),
    getValue(newValue) {
      if (newValue.length) {
        return newValue.split(' - ')
      }
      return []
    },
    generateStripedClass(index) {
      return this.isStriped && index % 2 !== 0
        ? 'bg-gray-200 dark:bg-dark-1'
        : ''
    },
    async handleGenerateInvoicesPDF() {
      this.filter['doc_type'] = this.$route.meta.doc_type

      if (import.meta.env.MODE !== 'development') {
        printJS(
          baseURL +
            'api/render/invoicing/?filter=' +
            JSON.stringify(this.filter)
        )
      } else {
        window.open(
          baseURL + 'api/render/invoicing?filter=' + JSON.stringify(this.filter)
        )
      }
    },
    handleGenerateInvoicesCSV() {
      this.fetchInvoicesCSV({
        doc_type: this.$route.meta.doc_type,
        company_id: this.filter.company_id,
        customer_search: this.filter.customer_search,
        status_code: this.filter.status_code,
        emission_date: this.filter.emission_date,
        due_date: this.filter.due_date,
        fiscal_year: this.filter.fiscal_year
      })
    },
    rowClick(val) {
      if (this.rowClick !== '') {
        this.$emit(this.rowClick, val)
      }
    },
    filterList(terms) {
      this.searchTerms = terms
    },
    handleSortColumn({ column, direction }) {
      if (direction === '-') {
        this.$emit('refresh-click')
        return
      }
      this.currentSort = column
      this.sortDirection = direction
    },
    handleFilterTable({ attribute, value }) {
      let temp_filter = this.filter
      if (value.length === 0) {
        delete temp_filter[attribute]
        this.filter = temp_filter
      } else {
        this.filter = { ...this.filter, [attribute]: value }
      }
      if (this.serverFilters && SERVER_FILTERS[attribute]) {
        this.$emit('server-filters-change', this.serverFilterParams())
      }
    },
    /** Query params of the API-side filters, as arrays of ids. */
    serverFilterParams() {
      const params = {}
      for (const [attribute, param] of Object.entries(SERVER_FILTERS)) {
        const values = this.filter[attribute]
        if (Array.isArray(values) && values.length) {
          params[param] = values.map((value) => value?.id ?? value)
        }
      }
      return params
    }
  },
  watch: {
    emission_date(newValue, oldValue) {
      this.handleFilterTable({
        attribute: 'emission_date',
        value: this.getValue(newValue)
      })
    },
    due_date(newValue, oldValue) {
      this.handleFilterTable({
        attribute: 'due_date',
        value: this.getValue(newValue)
      })
    }
  }
}
</script>
